import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Avatar,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const Settings: React.FC = () => {
  const { can } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { themeMode, setThemeMode, appName, logo, favicon, themeColor, sidebarLightBg, sidebarLightText, sidebarDarkBg, sidebarDarkText, refreshSettings } = useThemeContext();
  const { t } = useLanguage();
  
  const [distance, setDistance] = useState<number>(300);
  const [loading, setLoading] = useState(false);
  const [appSettingsLoading, setAppSettingsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    app_name: '',
    logo: null as File | null,
    favicon: null as File | null,
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);

  const [primaryColor, setPrimaryColor] = useState('#1976d2');
  const [sidebarColors, setSidebarColors] = useState({
    lightBg: '#ffffff',
    lightText: '#000000',
    darkBg: '#121212',
    darkText: '#ffffff',
  });

  const canView = can('settings.view');
  const canEditAppName = can('settings.update.app_name');
  const canEditLogo = can('settings.update.logo');
  const canEditFavicon = can('settings.update.favicon');
  const canEditTheme = can('settings.update.theme');
  const canEditDistance = can('settings.update.check_distance');
  const canEditThemeColor = can('settings.update.theme_color');
  const canEditSidebarLightBg = can('settings.update.sidebar_light_bg');
  const canEditSidebarLightText = can('settings.update.sidebar_light_text');
  const canEditSidebarDarkBg = can('settings.update.sidebar_dark_bg');
  const canEditSidebarDarkText = can('settings.update.sidebar_dark_text');

  useEffect(() => {
    const load = async () => {
      try {
        const [distanceRes, appRes] = await Promise.all([
          api.get('/settings/check-distance'),
          api.get('/settings/app'),
        ]);
        
        if (distanceRes.data?.value) {
          setDistance(Number(distanceRes.data.value));
        }
        
        if (appRes.data) {
          setFormData(prev => ({
            ...prev,
            app_name: appRes.data.app_name || '',
          }));
          if (appRes.data.logo) {
            setLogoPreview(logo || null);
          }
          if (appRes.data.favicon) {
            setFaviconPreview(favicon || null);
          }
          setPrimaryColor(appRes.data.theme_color || themeColor || '#1976d2');
          setSidebarColors({
            lightBg: appRes.data.sidebar_light_bg || sidebarLightBg || '#ffffff',
            lightText: appRes.data.sidebar_light_text || sidebarLightText || '#000000',
            darkBg: appRes.data.sidebar_dark_bg || sidebarDarkBg || '#121212',
            darkText: appRes.data.sidebar_dark_text || sidebarDarkText || '#ffffff',
          });
        }
      } catch (e) {
        console.error('Failed to load settings', e);
      }
    };
    load();
  }, [logo, favicon]);

  const handleSaveDistance = async () => {
    setLoading(true);
    try {
      await api.post('/settings/check-distance', { value: distance });
      notifySuccess(t('settings.messages.distance.updated'));
    } catch (e: any) {
      console.error(e);
      notifyError(e?.response?.data?.error || t('settings.messages.distance.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        notifyError(t('settings.messages.logo.sizeError'));
        return;
      }
      setFormData(prev => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 512 * 1024) {
        notifyError(t('settings.messages.favicon.sizeError'));
        return;
      }
      setFormData(prev => ({ ...prev, favicon: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
  };

  const handleRemoveFavicon = () => {
    setFormData(prev => ({ ...prev, favicon: null }));
    setFaviconPreview(null);
  };

  const handleSaveAppSettings = async () => {
    setAppSettingsLoading(true);
    try {
      const formDataToSend = new FormData();
      
      if (formData.app_name) {
        formDataToSend.append('app_name', formData.app_name);
      }
      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }
      if (formData.favicon) {
        formDataToSend.append('favicon', formData.favicon);
      }

      // Theme color
      if (canEditThemeColor && primaryColor) {
        formDataToSend.append('theme_color', primaryColor);
      }

      // Sidebar colors
      if (canEditSidebarLightBg && sidebarColors.lightBg) {
        formDataToSend.append('sidebar_light_bg', sidebarColors.lightBg);
      }
      if (canEditSidebarLightText && sidebarColors.lightText) {
        formDataToSend.append('sidebar_light_text', sidebarColors.lightText);
      }
      if (canEditSidebarDarkBg && sidebarColors.darkBg) {
        formDataToSend.append('sidebar_dark_bg', sidebarColors.darkBg);
      }
      if (canEditSidebarDarkText && sidebarColors.darkText) {
        formDataToSend.append('sidebar_dark_text', sidebarColors.darkText);
      }

      await api.post('/settings/app', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      notifySuccess(t('settings.messages.app.updated'));
      await refreshSettings();
      
      // Reset form
      setFormData({
        app_name: '',
        logo: null,
        favicon: null,
      });
      setLogoPreview(null);
      setFaviconPreview(null);
    } catch (e: any) {
      console.error(e);
      notifyError(e?.response?.data?.error || t('settings.messages.app.error'));
    } finally {
      setAppSettingsLoading(false);
    }
  };

  const handleThemeToggle = async () => {
    const newTheme = themeMode === 'dark' ? 'light' : 'dark';
    await setThemeMode(newTheme);
    await refreshSettings(); // Refresh settings to ensure theme is loaded
    notifySuccess(
      t('settings.messages.theme.changed').replace('{{mode}}', newTheme)
    );
  };

  const getStorageUrl = (path: string | null) => {
    if (!path) return null;
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://bms.sarahkw.com/backend/public/api';
    const storageBase = apiBaseUrl.replace('/api', '');
    return `${storageBase}/storage/${path}`;
  };

  if (!canView) {
    return <Typography>{t('settings.access.denied')}</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('settings.title')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* App Settings */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              {t('settings.app.cardTitle')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* App Name */}
              <TextField
                fullWidth
                label={t('settings.app.name.label')}
                value={formData.app_name}
                onChange={(e) => setFormData(prev => ({ ...prev, app_name: e.target.value }))}
                placeholder={appName}
                disabled={!canEditAppName}
                helperText={t('settings.app.name.helper')}
              />

              {/* Logo Upload */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('settings.app.logo.label')}
                </Typography>
                {logoPreview || logo ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={logoPreview || logo || undefined}
                      alt={t('settings.app.logo.label')}
                      sx={{ width: 80, height: 80 }}
                      variant="rounded"
                    />
                    <IconButton
                      color="error"
                      onClick={handleRemoveLogo}
                      disabled={!canEditLogo}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ) : null}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={!canEditLogo}
                  fullWidth
                >
                  {logoPreview || logo
                    ? t('settings.app.logo.change')
                    : t('settings.app.logo.upload')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('settings.app.logo.hint')}
                </Typography>
              </Box>

              {/* Favicon Upload */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('settings.app.favicon.label')}
                </Typography>
                {faviconPreview || favicon ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={faviconPreview || favicon || undefined}
                      alt={t('settings.app.favicon.label')}
                      sx={{ width: 32, height: 32 }}
                      variant="square"
                    />
                    <IconButton
                      color="error"
                      onClick={handleRemoveFavicon}
                      disabled={!canEditFavicon}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ) : null}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={!canEditFavicon}
                  fullWidth
                >
                  {faviconPreview || favicon
                    ? t('settings.app.favicon.change')
                    : t('settings.app.favicon.upload')}
                  <input
                    type="file"
                    hidden
                    accept="image/*,.ico"
                    onChange={handleFaviconChange}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('settings.app.favicon.hint')}
                </Typography>
              </Box>

              {/* Theme Toggle */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('settings.app.theme.label')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={themeMode === 'dark'}
                      onChange={handleThemeToggle}
                      disabled={!canEditTheme}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {themeMode === 'dark' ? <DarkMode /> : <LightMode />}
                      <Typography>
                        {themeMode === 'dark'
                          ? t('settings.app.theme.dark')
                          : t('settings.app.theme.light')}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Theme Color */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('settings.app.themeColor.label') !== 'settings.app.themeColor.label'
                    ? t('settings.app.themeColor.label')
                    : 'App Bar / Button Color'}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label={t('settings.app.themeColor.picker') !== 'settings.app.themeColor.picker'
                      ? t('settings.app.themeColor.picker')
                      : 'Primary Color'}
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    disabled={!canEditThemeColor}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: primaryColor,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {primaryColor}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('settings.app.themeColor.helper') !== 'settings.app.themeColor.helper'
                    ? t('settings.app.themeColor.helper')
                    : 'This color applies to the app bar, buttons, and other primary UI elements'}
                </Typography>
              </Box>

              {/* Sidebar Colors */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('settings.app.sidebar.colorsTitle')}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label={t('settings.app.sidebar.lightBg')}
                    type="color"
                    value={sidebarColors.lightBg}
                    onChange={(e) =>
                      setSidebarColors((prev) => ({ ...prev, lightBg: e.target.value }))
                    }
                    disabled={!canEditSidebarLightBg}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={t('settings.app.sidebar.lightText')}
                    type="color"
                    value={sidebarColors.lightText}
                    onChange={(e) =>
                      setSidebarColors((prev) => ({ ...prev, lightText: e.target.value }))
                    }
                    disabled={!canEditSidebarLightText}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label={t('settings.app.sidebar.darkBg')}
                    type="color"
                    value={sidebarColors.darkBg}
                    onChange={(e) =>
                      setSidebarColors((prev) => ({ ...prev, darkBg: e.target.value }))
                    }
                    disabled={!canEditSidebarDarkBg}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={t('settings.app.sidebar.darkText')}
                    type="color"
                    value={sidebarColors.darkText}
                    onChange={(e) =>
                      setSidebarColors((prev) => ({ ...prev, darkText: e.target.value }))
                    }
                    disabled={!canEditSidebarDarkText}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('settings.app.sidebar.helper')}
                </Typography>
              </Box>

              {(canEditAppName || canEditLogo || canEditFavicon || canEditTheme || canEditThemeColor || canEditSidebarLightBg || canEditSidebarLightText || canEditSidebarDarkBg || canEditSidebarDarkText) && (
                <Button
                  variant="contained"
                  onClick={handleSaveAppSettings}
                  disabled={appSettingsLoading}
                  fullWidth
                >
                  {appSettingsLoading
                    ? t('settings.app.save.saving')
                    : t('settings.app.save.button')}
                </Button>
              )}

              {!canEditAppName && !canEditLogo && !canEditFavicon && !canEditTheme && !canEditThemeColor && !canEditSidebarLightBg && !canEditSidebarLightText && !canEditSidebarDarkBg && !canEditSidebarDarkText && (
                <Typography variant="caption" color="text.secondary">
                  {t('settings.app.noPermission')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Check-in/Check-out Settings */}
        <Card>
          <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {t('settings.distance.cardTitle')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.distance.description')}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label={t('settings.distance.label')}
                  type="number"
                  value={distance}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setDistance(isNaN(v) ? 0 : v);
                  }}
                  disabled={!canEditDistance}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleSaveDistance}
                  disabled={!canEditDistance || loading}
                >
                  {t('settings.distance.save')}
                </Button>
              </Stack>
              {!canEditDistance && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('settings.distance.noPermission')}
                </Typography>
              )}
            </CardContent>
          </Card>
      </Box>
    </Box>
  );
};

export default Settings;
